/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchServices,
  setSelectedService,
  submitLoanApplication,
  clearSubmitStatus,
  fetchCustomerByUserId,
  clearCustomer,
} from "../redux/slices/loan/ApplyLoanSlice";
import "../assets/Loan/PersonalLoanForm.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";


export default function PersonalLoanForm() {
  const dispatch = useDispatch();
  const {
    services,
    selectedService,
    loading,
    error,
    submitStatus,
    customer,
    loanResponse,
  } = useSelector((state) => state.applyLoan);
  const { profile } = useSelector((state) => state.userProfile);

  // -------------------- States --------------------
  const [loanType, setLoanType] = useState("");
  const [loanScheme, setLoanScheme] = useState("");
  const [availableSchemes, setAvailableSchemes] = useState([]);
  const [loanAmount, setLoanAmount] = useState("");
  const [loanTenure, setLoanTenure] = useState("");
  const [loanEMIAmount, setLoanEMIAmount] = useState(0);
  const [loanInterest, setLoanInterest] = useState(0);
  const [loanPayable, setLoanPayable] = useState(0);

  const [formData, setFormData] = useState({
    fixedLoanFrequency: "",
    loanDisbursalMode: "",
    loanAppliedDate: "",
    loanPurpose: "",
    collectionLandmark: "",
    approvalDate: "",
    disbursalDate: "",
    status: "Pending",
    kycStatus: "Verified",
    fullName: "",
    branchCode: "BR001",
    mobileNumber: "",
    aadhaarNumber: "",
    panNumber: "",
    guarantorName: "",
    guarantorMobile: "",
    guarantorAadhaar: "",
    guarantorPAN: "",
    guarantorRelation: "",
    guarantorImageFile: null,
  });

  // -------------------- Fetch profile & services --------------------
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile?.superAdminId) {
      dispatch(fetchServices(String(profile.superAdminId)));
    }
  }, [dispatch, profile]);

  // -------------------- Prefill customer data --------------------
  useEffect(() => {
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        fullName: customer.fullName || "",
        mobileNumber: customer.mobileNumber || "",
        aadhaarNumber: customer.aadhaarNumber || "",
        panNumber: customer.panNumber || "",
        bankAccountNumber: customer.bankAccount || "",
        ifsc: customer.bankIFSC || "",
        guardianName: customer.guardianName || "",
        address: customer.address || "",
      }));
    } else if (!formData.userId) {
      setFormData({
        userId: "",
        fullName: "",
        mobileNumber: "",
        aadhaarNumber: "",
        panNumber: "",
        bankAccountNumber: "",
        ifsc: "",
        guardianName: "",
        address: "",
      });
    }
  }, [customer, formData.userId]);

  const handleUserIdChange = (e) => {
    const userId = e.target.value;
    setFormData((prev) => ({ ...prev, userId }));
    if (userId.length >= 5) {
      dispatch(fetchCustomerByUserId(userId));
    } else {
      dispatch(clearCustomer());
    }
  };

  
  const handleNonZeroInput = (value, setter) => {
    const val = Number(value);
    if (val > 0) setter(val);
    else setter("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizeLoanType = (type) => {
    if (!type) return "";
    const t = type.toLowerCase();
    if (t.includes("fixed")) return "Fixed";
    if (t.includes("personal")) return "Personal";
    return type;
  };


  // -------------------- Loan Schemes --------------------
  useEffect(() => {
    if (loanType) {
      const filtered = services.filter(
        (s) => s.serviceType === loanType && s.schemeStatus === "Active"
      );
      setAvailableSchemes(filtered);
    } else {
      setAvailableSchemes([]);
    }
    setLoanScheme("");
  }, [loanType, services]);

  useEffect(() => {
    if (loanScheme) dispatch(setSelectedService(loanScheme));
    else dispatch(setSelectedService(null));
  }, [loanScheme, dispatch]);




useEffect(() => {
  const isFixed = loanType.toLowerCase().includes("fixed");

  if (!isFixed && Number(loanAmount) > 0 && Number(loanTenure) > 0 && selectedService?.roi) {
    const principal = Number(loanAmount);
    const tenure = Number(loanTenure);
    const roi = Number(selectedService.roi); // Interest Rate %
    const roiType = selectedService.roiType?.toLowerCase() || "reducing"; // flat / reducing
    const recoveryFrequency = selectedService?.recoveryFrequency?.toLowerCase() || "daily"; // EMI frequency
    const roiFrequency = selectedService?.roiFrequency?.toLowerCase() || "monthly"; // ROI frequency

    // Mapping for frequency → periods per year
    const periodsPerYearMap = {
      daily: 365,
      weekly: 52,
      "bi-weekly": 26,
      "half-quarterly": 8,
      quarterly: 4,
      monthly: 12,
      yearly: 1,
    };
    const periodsPerYear = periodsPerYearMap[recoveryFrequency] || 12;

    // ✅ Calculate periodic rate (match EMI calculator logic)
    let periodRate = 0;
    if (roiFrequency === "yearly") {
      periodRate = roi / 100 / periodsPerYear;
    } else if (roiFrequency === "monthly") {
      switch (recoveryFrequency) {
        case "daily":
          // ✅ Simple division (same as your EMI calculator)
          periodRate = roi / 100 / 30;
          break;
        case "weekly":
          periodRate = roi / 100 / 4;
          break;
        case "bi-weekly":
          periodRate = roi / 100 / 2;
          break;
        case "half-quarterly":
          periodRate = roi / 100 / 2;
          break;
        case "quarterly":
          periodRate = (roi / 100) * 3 / 12;
          break;
        case "monthly":
          periodRate = roi / 100;
          break;
        case "yearly":
          periodRate = (roi / 100) * 12;
          break;
        default:
          periodRate = roi / 100;
      }
    }

    let emi = 0;
    let totalInterest = 0;
    let totalPayable = 0;

    // === FLAT TYPE ===
    if (roiType === "flat") {
      totalInterest = principal * periodRate * tenure;
      totalPayable = principal + totalInterest;
      emi = totalPayable / tenure;
    }

    // === REDUCING TYPE ===
    else if (roiType === "reducing") {
      emi =
        (principal * periodRate * Math.pow(1 + periodRate, tenure)) /
        (Math.pow(1 + periodRate, tenure) - 1);
      totalPayable = emi * tenure;
      totalInterest = totalPayable - principal;
    }

    // ✅ Update values
    setLoanEMIAmount(Number(emi.toFixed(2)));
    setLoanInterest(Number(totalInterest.toFixed(2)));
    setLoanPayable(Number(totalPayable.toFixed(2)));

    // === Optional: Generate EMI Schedule for debug/display ===
    const schedule = [];
    let balance = principal;
    let currentDate = new Date();

    for (let i = 1; i <= tenure; i++) {
      const interestComponent = balance * periodRate;
      const principalComponent = emi - interestComponent;
      balance -= principalComponent;

      schedule.push({
        emiNo: i,
        emi: Number(emi.toFixed(2)),
        principal: Number(principalComponent.toFixed(2)),
        interest: Number(interestComponent.toFixed(2)),
        balance: balance > 0 ? Number(balance.toFixed(2)) : 0,
        dueDate: new Date(currentDate).toISOString().split("T")[0],
      });

      // Increment based on frequency
      switch (recoveryFrequency) {
        case "daily":
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case "weekly":
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case "bi-weekly":
          currentDate.setDate(currentDate.getDate() + 14);
          break;
        case "monthly":
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        default:
          currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    console.table(schedule); // Optional: view full EMI breakdown in console

  } else {
    setLoanEMIAmount(0);
    setLoanInterest(0);
    setLoanPayable(0);
  }
}, [loanAmount, loanTenure, selectedService, loanType]);



  // -------------------- Submit Loan --------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!loanType || !loanScheme || !formData.fullName || !formData.loanAppliedDate) {
      toast.warn("Please fill all required fields!");
      return;
    }

    const isFixed = loanType.toLowerCase().includes("fixed");
    const payload = {
      userId: localStorage.getItem("userId"),
      superAdminId: profile.superAdminId,
      userName: formData.fullName,
      loanType,
      loanService: loanScheme,
      branchCode: formData.branchCode || "BR001",
      minLoanAmount: selectedService?.minAmount || 0,
      maxLoanAmount: selectedService?.maxAmount || 0,
      loanRoi: Number(selectedService?.roi) || 0,
      loanRecoveryFrequency: selectedService?.recoveryFrequency || "",
      schemeId: selectedService?.id || 0,
      tenuretype: selectedService?.tenureType || "",
      roiferequency: selectedService?.roiFrequency || "",
      loanRoiType: selectedService?.roiType || "",
      prefix: selectedService?.prefix || "",
      loanAmount: isFixed
        ? selectedService?.fixedLoanAmount || 0
        : Number(loanAmount) || 0,
      loanTenure: isFixed
        ? selectedService?.fixedLoanTenure || 0
        : Number(loanTenure) || 0,
      loanEmiAmount: isFixed
        ? selectedService?.loanEmiAmount || 0
        : Number(loanEMIAmount) || 0,
      loanDisbursalMode: formData.loanDisbursalMode,
      loanAppliedDate: new Date(formData.loanAppliedDate).toISOString().split("T")[0],
      loanInterest: isFixed ? 0 : Number(loanInterest) || 0,
      loanPayable: isFixed ? 0 : Number(loanPayable) || 0,
      loanPurpose: formData.loanPurpose,
      collectionLandmark: formData.collectionLandmark,
      status: "Pending",
      kycStatus: "Verified",
      customerId: formData.userId,
      mobileNumber: formData.mobileNumber,
      aadhar: formData.aadhaarNumber,
      panNumber: formData.panNumber,
      bankAccountNumber: formData.bankAccountNumber,
      ifsc: formData.ifsc,
      guardianName: formData.guardianName,
      address: formData.address,
      guarantorName: formData.guarantorName,
      guarantorMobile: formData.guarantorMobile,
      guarantorAadhaar: formData.guarantorAadhaar,
      guarantorPAN: formData.guarantorPAN,
      guarantorRelation: formData.guarantorRelation,
    };

    console.log("Submitting loan payload:", payload);
    toast.success("Loan Apply Successfully.")
    dispatch(submitLoanApplication(payload));
  };



//   useEffect(() => {
//     console.log("✅ ccccccccccccc, triggering nominee API...");
//     console.log(submitStatus);
//     console.log(loanResponse?.data);

//   if (submitStatus === "succeeded" && loanResponse?.data) {
//     console.log("✅ Loan success, triggering nominee API...");

//     const loanId =
//       loanResponse.data?.id ||
//       loanResponse.data?.loanId ||
//       loanResponse.data?.data?.id;

//     if (!loanId) {
//       console.error("❌ Loan ID missing — cannot create nominee.");
//       return;
//     }

//     const createNominee = async () => {
//       try {
//         const nomineeForm = new FormData();
//         nomineeForm.append("guarantorName", formData.guarantorName);
//         nomineeForm.append("guarantorMobile", formData.guarantorMobile);
//         nomineeForm.append("guarantorAadhar", formData.guarantorAadhaar);
//         nomineeForm.append("panNumber", formData.guarantorPAN);
//         nomineeForm.append("relationApplicant", formData.guarantorRelation);
//         nomineeForm.append("loanId", loanId);
//         nomineeForm.append("branchId", profile?.id || null);
//         nomineeForm.append("superAdminId", profile?.superAdminId || null);
//         nomineeForm.append("customerId", formData.userId);


//             if (formData.guarantorImageFile) {
//       nomineeForm.append("file", formData.guarantorImageFile);
//     }



//         const res = await fetch("https://narrowitsolution.in/api/nominee-crated", {
//           method: "POST",
//           body: nomineeForm,
//         });

//         const data = await res.json();
//         if (res.ok) {
//           console.log("✅ Nominee created successfully:", data);
//           alert("Laon  successfully submitted!");
//         } else {
//           console.error("❌ Nominee API failed:", data);
//         }
//       } catch (err) {
//         console.error("🚨 Error submitting nominee:", err);
//       }
//     };

//     createNominee();
//   }
// }, [submitStatus, loanResponse, formData, profile]);


useEffect(() => {
  if (submitStatus === "succeeded" && loanResponse?.data) {
    console.log("✅ Loan success, triggering nominee API...");

    const loanId =
      loanResponse.data?.id ||
      loanResponse.data?.loanId ||
      loanResponse.data?.data?.id;

    if (!loanId) {
      toast.error("Loan ID missing — cannot create nominee.");
      return;
    }

    const createNominee = async () => {
      try {
        const nomineeForm = new FormData();
        nomineeForm.append("guarantorName", formData.guarantorName);
        nomineeForm.append("guarantorMobile", formData.guarantorMobile);
        nomineeForm.append("guarantorAadhar", formData.guarantorAadhaar);
        nomineeForm.append("panNumber", formData.guarantorPAN);
        nomineeForm.append("relationApplicant", formData.guarantorRelation);
        nomineeForm.append("loanId", loanId);
        nomineeForm.append("branchId", profile?.id || null);
        nomineeForm.append("superAdminId", profile?.superAdminId || null);
        nomineeForm.append("customerId", formData.userId);

        if (formData.guarantorImageFile) {
          nomineeForm.append("file", formData.guarantorImageFile);
        }

        toast.info("Creating nominee, please wait...");

        const res = await fetch("https://narrowitsolution.in/api/nominee-crated", {
          method: "POST",
          body: nomineeForm,
        });

        const data = await res.json();

        if (res.ok) {
          toast.success("✅ Loan and Nominee submitted successfully!");
           resetForm(); // 🔥 C
           dispatch(clearSubmitStatus()); // Optional - reset redux status

        } else {
          console.error(`❌ Nominee creation failed: ${data?.message || "Error"}`);
        }
      } catch (err) {
        console.error("🚨 Error submitting nominee:", err);
        toast.error("Error submitting nominee!");
      }
    };

    createNominee();
  }
}, [submitStatus, loanResponse,formData, profile]);




console.log("selected service ", selectedService);

  // -------------------- Render --------------------
  const isFixedLoan = loanType.toLowerCase().includes("fixed");


const resetForm = () => {
  setLoanType("");
  setLoanScheme("");
  setAvailableSchemes([]);
  setLoanAmount("");
  setLoanTenure("");
  setLoanEMIAmount(0);
  setLoanInterest(0);
  setLoanPayable(0);
  setFormData({
    fixedLoanFrequency: "",
    loanDisbursalMode: "",
    loanAppliedDate: "",
    loanPurpose: "",
    collectionLandmark: "",
    approvalDate: "",
    disbursalDate: "",
    status: "Pending",
    kycStatus: "Verified",
    fullName: "",
    branchCode: "BR001",
    mobileNumber: "",
    aadhaarNumber: "",
    panNumber: "",
    guarantorName: "",
    guarantorMobile: "",
    guarantorAadhaar: "",
    guarantorPAN: "",
    guarantorRelation: "",
    guarantorImageFile: null,
    userId: "",
    bankAccountNumber: "",
    ifsc: "",
    guardianName: "",
    address: "",
  });
};


  return (
    <div className="PersonalLoanForm-container">
      <h1>Loan Application Form</h1>
   {(loading || submitStatus === "loading") && (
  <div className="loader-overlay">
    <ClipLoader size={60} color="#3498db" />
  </div>
)}


      <form className="PersonalLoanForm-form" onSubmit={handleSubmit}>
        {/* Loan Options */}
        <section className="PersonalLoanForm-section">
          <h2>Loan Options & Schemes</h2>
          <div className="PersonalLoanForm-loan-options">
            <div className="PersonalLoanForm-form-group">
              <label>Loan Type</label>
              <select
                value={loanType}
                onChange={(e) => setLoanType(e.target.value)}
                required
              >
                <option value="">Select Loan Type</option>
                {[...new Set(services.map((s) => s.serviceType))].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="PersonalLoanForm-form-group">
              <label>Loan Scheme</label>
              <select
                value={loanScheme}
                onChange={(e) => setLoanScheme(e.target.value)}
                disabled={!availableSchemes.length}
                required
              >
                <option value="">Select Scheme</option>
                {availableSchemes.map((s) => (
                  <option key={s.serviceName} value={s.serviceName}>
                    {s.serviceName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>



        
      {/* Loan Details */}
        <section className="PersonalLoanForm-section">
         <h2>Loan Details</h2>
         <div className="PersonalLoanForm-loan-details-grid">
           {/* Fixed Loan: show only Fixed Loan Amount & EMI */}
            <div className="PersonalLoanForm-form-group">
             <label>
               {isFixedLoan ? "Fixed Loan Amount" : "Minimum Loan Amount"}
             </label>
             <input
                type="number"
                value={
                  isFixedLoan
                    ? selectedService?.fixedLoanAmount ?? ""
                    : selectedService?.minAmount ?? ""
                }
                readOnly
              />
            </div>

            <div className="PersonalLoanForm-form-group">
              <label>
                {isFixedLoan ? "Fixed Loan EMI Amount" : "Maximum Loan Amount"}
              </label>
              <input
                type="number"
                value={
                  isFixedLoan
                    ? selectedService?.fixedEmiAmount ?? ""
                    : selectedService?.maxAmount ?? ""
                }
                readOnly
              />
            </div>

            <div className="PersonalLoanForm-form-group">
              <label>Loan Interest Rate (%)</label>
              <input
                type="number"
                value={selectedService?.roi || ""}
                readOnly
              />
            </div>

            <div className="PersonalLoanForm-form-group">
              <label>ROI  Frequency</label>
              <input
                type="text"
                value={selectedService?.roiFrequency || ""}
                readOnly
              />
            </div>

            {/* Other loan properties read-only */}
            {["roiType", "fixedLoanFrequency", "isFixedTenure", "maxTenure"].map(
              (id) => (
                <div className="PersonalLoanForm-form-group" key={id}>
                   <label>
                    {{
                      roiType: "ROI Type",
                      fixedLoanFrequency: "Fixed Loan Frequency",
                      isFixedTenure: "Is Fixed Tenure",
                       maxTenure: `Maximum Tenure (${selectedService?.tenureType || ""})`,
                    }[id]}
                  </label>
                  <input
                    type="text"
                    value={
                      id === "isFixedTenure"
                        ? selectedService?.[id]
                          ? "Yes"
                          : "No"
                        : selectedService?.[id] ?? ""
                    }
                    readOnly
                  />
                </div>
              )
            )}

            {/* Loan Amount/Tenure inputs - hide for Fixed Loan */}
            {!isFixedLoan &&
              [
                {
                  id: "loanAmount",
                  label: "Loan Amount",
                  value: loanAmount,
                  set: setLoanAmount,
                },
                {
                  id: "loanTenure",
                  label: `Loan Tenure (${selectedService?.tenureType || ""})`,
                  value: loanTenure,
                  set: setLoanTenure,
                },
              ].map(({ id, label, value, set }) => (
                <div className="PersonalLoanForm-form-group" key={id}>
                  <label htmlFor={id}>{label}</label>
                  <input
                    type="number"
                    id={id}
                    name={id}
                    value={value}
                    min={1}
                    onChange={(e) => handleNonZeroInput(e.target.value, set)}
                  />
                </div>
              ))}

            {/* Calculated amounts: EMI, Interest, Payable - hide for Fixed Loan */}
            {!isFixedLoan &&
              [
                { id: "loanEMIAmount", label: "Loan EMI Amount", value: loanEMIAmount },
                { id: "loanInterest", label: "Loan Interest Amount", value: loanInterest },
                { id: "loanPayable", label: "Loan Payable Amount", value: loanPayable },
              ].map(({ id, label, value }) => (
                <div className="PersonalLoanForm-form-group" key={id}>
                  <label htmlFor={id}>{label}</label>
                  <input type="number" id={id} name={id} value={value} readOnly />
                </div>
              ))}

            {/* Selects for frequencies and modes */}
            {[
              // {
              //   id: "fixedLoanFrequency",
              //   label: "Fixed Loan Frequency",
              //   options: ["Monthly", "Quarterly", "Half-Yearly", "Yearly"],
              // },
              // {
              //   id: "loanRoiType",
              //   label: "Loan ROI Type",
              //   options: ["Fixed", "Floating"],
              // },
              {
                id: "loanDisbursalMode",
                label: "Loan Disbursal Mode",
                options: ["Bank Transfer", "Cheque", "Cash"],
              },
            ].map(({ id, label, options }) => (
              <div className="PersonalLoanForm-form-group" key={id}>
                <label htmlFor={id}>{label}</label>
                <select
                  id={id}
                  name={id}
                  value={formData[id] || ""}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select {label}
                  </option>
                  {options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            <div className="PersonalLoanForm-form-group">
              <label htmlFor="loanAppliedDate">Loan Applied Date</label>
              <input
                type="date"
                id="loanAppliedDate"
                name="loanAppliedDate"
                value={formData.loanAppliedDate || ""}
                onChange={handleChange}
              />
            </div>

            <div className="PersonalLoanForm-form-group ">
              <label htmlFor="loanPurpose">Loan Purpose</label>
              <input
                type="text"
                id="loanPurpose"
                name="loanPurpose"
                placeholder="Reason or description"
                value={formData.loanPurpose || ""}
                onChange={handleChange}
              />
            </div>

            {/* <div className="PersonalLoanForm-form-group full-width">
              <label htmlFor="collectionLandmark">Select Collection Landmark</label>
              <select
                id="collectionLandmark"
                name="collectionLandmark"
                value={formData.collectionLandmark || ""}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Landmark
                </option>
                <option value="Near Bank Branch">Near Bank Branch</option>
                <option value="Community Center">Community Center</option>
                <option value="Local Shop">Local Shop</option>
                <option value="Other">Other</option>
              </select>
            </div> */}

            <div className="PersonalLoanForm-form-group ">
  <label htmlFor="collectionLandmark">Collection Landmark</label>
  <input
    type="text"
    id="collectionLandmark"
    name="collectionLandmark"
    placeholder="Enter collection landmark (e.g., Near Post Office)"
    value={formData.collectionLandmark || ""}
    onChange={handleChange}
  />
</div>





          </div>
        </section>


        {/* User Details */}
        <section className="PersonalLoanForm-section">
          <h2>User Details</h2>
          <div className="PersonalLoanForm-form-group">
            <label>Customer ID</label>
            <input
              type="text"
              name="userId"
              value={formData.userId || ""}
              onChange={handleUserIdChange}
              placeholder="Enter Customer ID"
              required
            />
          </div>

          <div className="PersonalLoanForm-loan-details-grid">
            {[
              { id: "fullName", label: "Full Name" },
              { id: "mobileNumber", label: "Mobile Number" },
              { id: "aadhaarNumber", label: "Aadhaar Number" },
              { id: "panNumber", label: "PAN Number" },
              { id: "bankAccountNumber", label: "Bank Account Number" },
              { id: "ifsc", label: "IFSC" },
              { id: "guardianName", label: "Guardian Name" },
              { id: "address", label: "Address" },
            ].map(({ id, label }) => (
              <div className="PersonalLoanForm-form-group" key={id}>
                <label>{label}</label>
                <input
                  type="text"
                  name={id}
                  value={formData[id] || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [id]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </section>

        {/* Guarantor */}
        <section className="PersonalLoanForm-section">
          <h2>Guarantor Details</h2>
          <div className="PersonalLoanForm-loan-details-grid">
            {[
              { id: "guarantorName", label: "Guarantor Name" },
              { id: "guarantorMobile", label: "Mobile Number" },
              { id: "guarantorAadhaar", label: "Aadhaar Number" },
              { id: "guarantorPAN", label: "PAN Number" },
              { id: "guarantorRelation", label: "Relation with Applicant" },
            ].map(({ id, label }) => (
              <div key={id} className="PersonalLoanForm-form-group">
                <label>{label}</label>
                <input
                  type="text"
                  name={id}
                  value={formData[id] || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [id]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
            <div className="PersonalLoanForm-form-group">
              <label>Guarantor Photo</label>
              <input
                type="file"
                // required
                accept="image/*"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    guarantorImageFile: e.target.files[0],
                  }))
                }
              />
            </div>
          </div>

          {formData.guarantorImageFile && (
            <img
              src={URL.createObjectURL(formData.guarantorImageFile)}
              alt="Preview"
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "10px",
                objectFit: "cover",
                marginTop: "10px",
              }}
            />
          )}
        </section>
        <div className="PersonalLoanForm-submit-wrapper">
        <button
          type="submit"
          className="PersonalLoanForm-apply-btn"
          disabled={submitStatus === "loading"}
        >
          {submitStatus === "loading" ? "Submitting..." : "Apply"}
        </button>
        </div>
      </form>

    </div>
  );
}
